import type { ListBuilderConfiguration } from '../types';
import {
	PURCHASE_ORDER_RECEIPTS_MAIN_COLUMNS,
	PURCHASE_ORDER_RECEIPTS_MAIN_REQUIRED_FIELDS,
} from './purchase_order_receipts_main.columns';

/**
 * Vendor-embedded Purchase Order Receipts list (Phase 9). Mounted on
 * the Vendor detail "Receipts" tab. Reuses the column set + required
 * fields from the tenant-wide main config so column changes only need
 * to be made in one place.
 *
 * Vendor scoping is applied via the queryFn at the tab layer (the tab
 * passes `filters: { receiptVendor: [vendorId] }` to the standard
 * GET /api/purchase-order-receipts endpoint). Single-tab embedded
 * shape — no PO list-style tab proliferation needed since the parent
 * is already vendor-scoped.
 */
export const PURCHASE_ORDER_RECEIPTS_VENDOR_EMBEDDED_CONFIG: ListBuilderConfiguration =
	{
		lists: [
			{
				id: 'default-purchase-order-receipts-vendor-embedded',
				cacheTTL: 300000,
				name: 'Vendor Receipts',
				type: 'default',
				active: true,
				columns: PURCHASE_ORDER_RECEIPTS_MAIN_COLUMNS,
				description:
					'Receipts from this vendor — one row per receipt event, in reverse-chronological order.',
				defaultSortId: 'receipt-date-desc',
				requiredFields: PURCHASE_ORDER_RECEIPTS_MAIN_REQUIRED_FIELDS,
				sortOrder: 0,
			},
		],
		paging: {
			defaultPageSize: 25,
			pageSizeOptions: [25, 50, 100],
		},
		filters: {
			activeFilters: [
				'receiptKeywords',
				'receiptDispositionStatus',
				'receiptDateRange',
			],
			defaultFilters: {},
		},
		objects: [
			{
				baseUrl: '/inventory/receipts/:id',
				objectLabel: 'Purchase Order Receipt',
				objectTable: 'purchase_order_receipt',
				selectedFields: [
					{ fieldName: '_id', fieldLabel: 'ID' },
					{ fieldName: 'externalId', fieldLabel: 'Receipt #' },
					{ fieldName: 'receiptDate', fieldLabel: 'Receipt Date' },
					{ fieldName: 'dispositionStatus', fieldLabel: 'Disposition' },
				],
			},
		],
		tableSorts: [
			{
				id: 'receipt-date-desc',
				name: 'Receipt Date (newest)',
				fields: [
					{
						objectTable: 'purchase_order_receipt',
						direction: 'DESC',
						fieldName: 'receiptDate',
					},
				],
				description: 'Most recent receipts first',
			},
		],
	};
